import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Lightbulb, Wrench, Bug } from 'lucide-react';
import { useCreateFeatureRequest } from '../../hooks/use-feature-requests';
import type { FeatureRequestType } from '../../types';

const TYPE_OPTIONS: Array<{ value: FeatureRequestType; label: string; icon: typeof Lightbulb; hint: string }> = [
  { value: 'FEATURE', label: 'Funcționalitate nouă', icon: Lightbulb, hint: 'Ceva ce nu există încă' },
  { value: 'CHANGE', label: 'Modificare', icon: Wrench, hint: 'Schimbare la ceva existent' },
  { value: 'BUG', label: 'Bug', icon: Bug, hint: 'Ceva ce nu funcționează corect' },
];

export function NewFeatureRequestPage() {
  const navigate = useNavigate();
  const create = useCreateFeatureRequest();
  const [type, setType] = useState<FeatureRequestType>('FEATURE');
  const [message, setMessage] = useState('');
  const [touched, setTouched] = useState(false);

  const valid = message.trim().length >= 3;

  const submit = () => {
    setTouched(true);
    if (!valid || create.isPending) return;
    create.mutate(
      { type, initialMessage: message.trim() },
      {
        onSuccess: ({ data }) => {
          navigate(`/admin/feature-requests/${data.id}/chat`);
        },
      },
    );
  };

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col justify-center px-4 py-8 md:px-6">
      <button
        type="button"
        onClick={() => navigate('/admin/feature-requests')}
        className="mb-6 inline-flex w-fit items-center gap-1.5 text-xs font-medium text-[color:var(--ink-muted)] transition-colors hover:text-[color:var(--ink-strong)]"
      >
        <ArrowLeft size={14} /> Înapoi la cereri
      </button>

      <div className="app-shell-panel rounded-[1.75rem] px-6 py-7 md:px-8 md:py-8">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-accent text-white shadow-[var(--shadow-soft)]">
            <Sparkles size={20} />
          </span>
          <div>
            <h1 className="text-xl font-bold text-[color:var(--ink-strong)]">Sugestie nouă</h1>
            <p className="text-sm text-[color:var(--ink-muted)]">
              Descrie pe scurt ideea — Analiza preia de aici și te va întreba critic.
            </p>
          </div>
        </div>

        {/* Type selector */}
        <div className="mt-6">
          <p className="mb-2.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-[color:var(--ink-soft)]">
            Tipul cererii
          </p>
          <div className="grid gap-2.5 sm:grid-cols-3">
            {TYPE_OPTIONS.map((opt) => {
              const active = type === opt.value;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setType(opt.value)}
                  aria-pressed={active}
                  className={
                    active
                      ? 'flex flex-col items-start gap-2 rounded-2xl border-2 border-primary bg-primary/6 px-4 py-3.5 text-left'
                      : 'flex flex-col items-start gap-2 rounded-2xl border-2 border-[color:var(--surface-line)] bg-[var(--surface-panel)] px-4 py-3.5 text-left transition-colors hover:border-[color:var(--surface-line-strong)]'
                  }
                >
                  <span
                    className={
                      active
                        ? 'flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white'
                        : 'flex h-8 w-8 items-center justify-center rounded-full bg-[var(--surface-panel-strong)] text-[color:var(--ink-muted)]'
                    }
                  >
                    <Icon size={15} />
                  </span>
                  <span>
                    <span className={`block text-sm font-semibold ${active ? 'text-primary' : 'text-[color:var(--ink-strong)]'}`}>
                      {opt.label}
                    </span>
                    <span className="mt-0.5 block text-[11px] text-[color:var(--ink-muted)]">{opt.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Initial message */}
        <div className="mt-6">
          <label
            htmlFor="fr-initial-message"
            className="mb-2.5 block text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-[color:var(--ink-soft)]"
          >
            Descrie ideea
          </label>
          <textarea
            id="fr-initial-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onBlur={() => setTouched(true)}
            rows={4}
            placeholder="Ex: Vreau să pot exporta dashboard-ul în PDF ca să-l trimit managementului…"
            className={`block w-full resize-none rounded-2xl border bg-[var(--surface-panel-strong)] px-4 py-3 text-sm text-[color:var(--ink-strong)] placeholder:text-[color:var(--ink-soft)] transition-colors focus:outline-none focus:ring-2 ${
              touched && !valid
                ? 'border-error focus:ring-error'
                : 'border-[color:var(--surface-line)] focus:border-[color:var(--surface-line-strong)] focus:ring-[color:var(--surface-focus)]'
            }`}
          />
          {touched && !valid && (
            <p className="mt-1.5 text-xs text-error">Descrie pe scurt ideea (minim 3 caractere).</p>
          )}
        </div>

        {/* Submit */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <p className="hidden text-xs text-[color:var(--ink-soft)] sm:block">
            AI-ul verifică ideea în cod și în date, apoi pregătește o specificație.
          </p>
          <button
            type="button"
            onClick={submit}
            disabled={!valid || create.isPending}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-white shadow-[var(--shadow-soft)] transition-all hover:-translate-y-px hover:bg-primary-light disabled:pointer-events-none disabled:opacity-50"
          >
            {create.isPending ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                Se creează…
              </>
            ) : (
              <>
                <Sparkles size={16} />
                Începe conversația
              </>
            )}
          </button>
        </div>

        {create.isError && (
          <p className="mt-4 rounded-xl bg-error/8 px-4 py-3 text-sm text-error">
            {create.error?.message ?? 'Nu am putut crea cererea. Încearcă din nou.'}
          </p>
        )}
      </div>
    </div>
  );
}
